/**
 * usePageTracking — emit a `page.viewed` event on every route change.
 *
 * Side-channel, non-blocking, fire-and-forget (uses tracker.track which
 * never awaits and never throws). Call once from the app root (e.g.
 * inside PortalLayout). Covers 100% of the portal's navigation events
 * for free — every dashboard route, notebook route, pipeline route,
 * settings route, every admin page, every list-vs-detail transition,
 * all as a single line of code.
 *
 * The `path` property is the raw location.pathname, no query string
 * and no hash. That means `/bi/abc-123` and `/bi/xyz-456` emit the
 * same event type (`page.viewed`) with different `path` values, which
 * you can group on at query time.
 *
 * For finer-grained questions — e.g. "which dashboard was viewed?" —
 * use the explicit `dashboard.viewed` event with `entity_id` that
 * still fires from the page components. This hook is additive to
 * those, not a replacement.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { tracker } from './tracker';
import { Events } from './events';

export function usePageTracking(): void {
  const location = useLocation();

  useEffect(() => {
    tracker.track(Events.PAGE_VIEWED, {
      properties: {
        path: location.pathname,
      },
    });
    // Only re-emit when the path changes. Query params and hashes
    // don't count as a new page — if they matter for a specific
    // analytics use case, fire a distinct event at the call site.
  }, [location.pathname]);
}
